import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { IconEdit, IconTeams } from '../../../common/icons';
import DeactivateButton from '../../../common/components/deactivate/deactivate-button';
import { Gate } from '../../../global/contexts/auth/auth';
import { useClientsList } from '../../clients/contexts/clients';
import { useProjectAssignments } from '../../project-teams/contexts/assignments';
import { PROJECTS_PAGE_SIZE, useProjectsList } from '../contexts/projects';
import { repositoryKeys } from '../../repositories/queries/keys';
import { projectKeys } from '../queries/keys';
import { projectPaths } from '../queries/paths';
import { BOOTSTRAP_PHASE_LABEL, PROJECT_STATE_OPTIONS } from '../utils/form';
import SearchBox from '../../../common/components/search-box/search-box';

import '../styles/overview.css';

// Cards, like clients and teams. And the FIRST list with a filter bar, because
// this is the first endpoint that takes filters: client_id, project_state and
// bootstrap_phase. The other sections have no search box for the opposite
// reason — theirs would have to filter the page already fetched, and lie.
//
// Filters live in the URL, next to the page: a link to "the paused ones of
// this client" has to survive a reload and be worth sending to somebody.

const PROJECT_STATE_LABEL = Object.fromEntries(
    PROJECT_STATE_OPTIONS.map(option => [option.value, option.label]),
);

// One request for the whole page, not one per card: the list answers
// client_id and the name lives in the clients table.
const CLIENT_LOOKUP_LIMIT = 100;

// How many teams reach this project. Read through the assignments hook and not
// a copy of it, so assigning or removing a team from the project's own screen
// moves this number too — same key, same invalidation.
//
// ONLY ACTIVE ASSIGNMENTS. Removing one is a soft delete (`status` 1) and the
// list endpoint still returns it; the scope resolver reads `status = 2`, and a
// team that no longer reaches the project must not keep it looking staffed.
//
// ZERO IS THE ALARM. A project nobody is assigned to is a project whose tasks
// no team can take, so the indicator turns red instead of reading as a quiet 0.
function TeamsIndicator({ projectId }) {
    const { data, isLoading, error } = useProjectAssignments(projectId);

    if (isLoading) {
        return <span className="project-teams project-teams--loading" aria-busy="true">…</span>;
    }
    if (error) {
        return (
            <span className="project-teams project-teams--unknown" title="No se pudieron contar los equipos">
                <IconTeams /> ?
            </span>
        );
    }

    const count = (data?.items ?? []).filter(item => item.status === 2).length;
    const isEmpty = count === 0;
    const text = isEmpty ? 'Sin equipos' : `${count} ${count === 1 ? 'equipo' : 'equipos'}`;

    return (
        <span
            className={`project-teams${isEmpty ? ' project-teams--empty' : ''}`}
            title={isEmpty ? 'Ningún equipo asignado: nadie puede tomar sus tareas' : text}
        >
            <IconTeams />
            <span className="project-teams__count">{text}</span>
        </span>
    );
}

function ProjectCard({ project, clientName }) {
    const phase = BOOTSTRAP_PHASE_LABEL[project.bootstrap_phase] ?? project.bootstrap_phase;

    return (
        <article className="project-card">
            <header className="project-card__head">
                <span className="project-card__identity">
                    <span className="project-card__name">{project.name}</span>
                    <span className="project-card__slug">{project.slug}</span>
                </span>

                <span className="row-actions">
                    <Gate domain="projects:write">
                        <Link
                            to={`/admin/projects/${project.id}`}
                            className="project-card__edit"
                            aria-label={`Editar ${project.name}`}
                            title="Editar"
                        >
                            <IconEdit />
                        </Link>
                    </Gate>
                    {/* The one deactivation that takes more than its row: Core
                        closes the project AND deactivates its repositories. So
                        the name is typed, not clicked through. */}
                    <Gate domain="projects:admin">
                        <DeactivateButton
                            name={project.name}
                            noun="proyecto"
                            path={projectPaths.detail(project.id)}
                            invalidate={[projectKeys.all, repositoryKeys.all]}
                            confirmName
                            consequence="El proyecto queda inactivo y cerrado, y todos sus repositorios quedan inactivos con él. Nada se borra: las filas y su historia se conservan, y se reactivan desde su edición, en «Estado del registro»."
                        />
                    </Gate>
                </span>
            </header>

            <div className="project-card__body">
                <span className="project-card__block-label">Cliente</span>
                <p className="project-card__client">
                    {clientName || `Cliente #${project.client_id}`}
                </p>

                {project.description && (
                    <p className="project-card__description">{project.description}</p>
                )}
            </div>

            <footer className="project-card__foot">
                <span className="project-card__fact">
                    <span className="project-card__block-label">Estado</span>
                    <span className={`project-card__state project-card__state--${project.project_state}`}>
                        {PROJECT_STATE_LABEL[project.project_state] ?? project.project_state}
                    </span>
                </span>

                <span className="project-card__fact">
                    <span className="project-card__block-label">Fase</span>
                    <span className="project-card__value">{phase || '—'}</span>
                </span>

                <span className="project-card__fact">
                    <span className="project-card__block-label">Equipos</span>
                    <TeamsIndicator projectId={project.id} />
                </span>
            </footer>
        </article>
    );
}

export default function ProjectsOverview({ embedded = false }) {
    const [params, setParams] = useSearchParams();

    const page = Math.max(Number(params.get('page')) || 1, 1);
    const clientId = params.get('client') || '';
    const projectState = params.get('state') || '';
    const q = params.get('q') || '';
    const offset = (page - 1) * PROJECTS_PAGE_SIZE;

    const { data, isLoading, isFetching, error } = useProjectsList({
        limit: PROJECTS_PAGE_SIZE,
        offset,
        clientId,
        projectState,
        q,
    });

    const { data: clientPage } = useClientsList({ limit: CLIENT_LOOKUP_LIMIT, offset: 0 });

    const clientNames = useMemo(() => {
        const map = new Map();
        for (const client of clientPage?.items ?? []) map.set(client.id, client.name);
        return map;
    }, [clientPage]);

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;
    const lastPage = useMemo(
        () => Math.max(Math.ceil(total / PROJECTS_PAGE_SIZE), 1),
        [total],
    );

    // Changing a filter always returns to page one: staying on page 3 of a
    // narrower result set lands on an empty screen that looks like "there is
    // nothing", when there is.
    const setFilter = (key, value) => {
        const target = new URLSearchParams(params);
        if (value) target.set(key, value);
        else target.delete(key);
        target.delete('page');
        setParams(target);
    };

    const goToPage = (next) => {
        const target = new URLSearchParams(params);
        if (next <= 1) target.delete('page');
        else target.set('page', String(next));
        setParams(target);
    };

    const hasFilters = !!(clientId || projectState || q);

    if (isLoading && !data) return <p className="projects__loading">Cargando proyectos...</p>;

    if (error) {
        return (
            <p className="projects__error">
                {error.message || 'No se pudieron cargar los proyectos'}
            </p>
        );
    }

    return (
        <section className="projects">
            {/* Embebido en la sección, el título y el botón los pone ella:
                repetirlos sería dos encabezados para una pantalla. Suelto, la
                lista sigue trayendo los suyos. */}
            {embedded
                ? (
                    <p className="projects__count">
                        {total} {total === 1 ? 'proyecto' : 'proyectos'}
                    </p>
                )
                : (
                    <header className="projects__header">
                        <div className="projects__heading">
                            <span className="projects__count">
                                {total} {total === 1 ? 'proyecto' : 'proyectos'}
                            </span>
                            <h2 className="projects__title">Proyectos</h2>
                        </div>

                        <Gate domain="projects:write">
                            <Link to="/admin/projects/new" className="projects__new">+ Nuevo proyecto</Link>
                        </Gate>
                    </header>
                )}

            <SearchBox placeholder="Buscar por nombre o slug" />

            <div className="projects__filters">
                <select
                    className="projects__filter"
                    value={clientId}
                    onChange={e => setFilter('client', e.target.value)}
                    aria-label="Filtrar por cliente"
                >
                    <option value="">Todos los clientes</option>
                    {(clientPage?.items ?? []).map(client => (
                        <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                </select>

                <select
                    className="projects__filter"
                    value={projectState}
                    onChange={e => setFilter('state', e.target.value)}
                    aria-label="Filtrar por estado"
                >
                    <option value="">Todos los estados</option>
                    {PROJECT_STATE_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>

                {hasFilters && (
                    <button
                        type="button"
                        className="projects__filter-clear"
                        onClick={() => setParams(new URLSearchParams())}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {!items.length && (
                <p className="projects__empty">
                    {hasFilters
                        ? 'Ningún proyecto coincide con estos filtros.'
                        : 'Todavía no hay proyectos cargados.'}
                </p>
            )}

            <div className={`projects__grid ${isFetching ? 'projects__grid--refreshing' : ''}`}>
                {items.map(project => (
                    <ProjectCard
                        key={project.id}
                        project={project}
                        clientName={clientNames.get(project.client_id)}
                    />
                ))}
            </div>

            {total > PROJECTS_PAGE_SIZE && (
                <nav className="projects__pager">
                    <button
                        type="button"
                        className="projects__pager-button"
                        onClick={() => goToPage(page - 1)}
                        disabled={page <= 1}
                    >
                        Anterior
                    </button>

                    <span className="projects__pager-position">
                        Página {page} de {lastPage}
                    </span>

                    <button
                        type="button"
                        className="projects__pager-button"
                        onClick={() => goToPage(page + 1)}
                        disabled={page >= lastPage}
                    >
                        Siguiente
                    </button>
                </nav>
            )}
        </section>
    );
}
